import './FilterMenuItem.scss';
import classNames from 'classnames';
import { prefixClass } from 'lib/utils';
import { useState, type ReactNode } from 'react';
import { useDroppable } from '@dnd-kit/core';
import { QueueView } from '@hone-automation/common';
import { CountBadge, Tooltip } from '../../../Common';
import { useQueueContext } from '../../../../Queue/QueueContext';
import { IconChevron, IconChevronDown } from 'components/Icons';

type FilterMenuItemVariant = 'default' | 'parent' | 'child';

interface FilterMenuItemProps {
  /** Filter ID for drop target identification (required for default/child, optional for parent) */
  filterId?: QueueView | string;
  /** Icon to display on the left (not shown for child variant) */
  icon?: ReactNode;
  /** Label text */
  label: string;
  /** Count to display in the badge */
  count?: number;
  /** Whether the item is selected */
  selected?: boolean;
  /** Whether this filter can accept the current drag item */
  canDrop?: boolean;
  /** Click handler */
  onClick?: () => void;
  /** Additional class name */
  className?: string;
  /** Variant: default (normal), parent (collapsible with children), child (indented) */
  variant?: FilterMenuItemVariant;
  /** Children to render when variant is 'parent' and expanded */
  children?: ReactNode;
  /** Whether parent is expanded (only for parent variant) */
  defaultExpanded?: boolean;
  /** Hide the count display (e.g., for Closed filter) */
  hideCount?: boolean;
  /** Show alert styling with pulse animation (e.g., for blockers with items) */
  alert?: boolean;
  /** Action icon shown on hover (e.g., dissolve button for groups) */
  actionIcon?: ReactNode;
  /** Tooltip text for the action button */
  actionTooltip?: string;
  /** Handler for the action button click */
  onAction?: () => void;
  /** Custom tooltip content for the label (defaults to label text) */
  labelTooltip?: string;
}

/**
 * FilterMenuItem - A clickable filter item with icon, label, and optional count badge
 * Also acts as a drop target for drag and drop operations
 *
 * Variants:
 * - default: Standard filter item (Blockers, Me, etc.)
 * - parent: Collapsible item with nested children (Groups)
 * - child: Indented item under a parent (group items)
 */
const FilterMenuItem = ({
  filterId,
  icon,
  label,
  count,
  selected = false,
  canDrop = true,
  onClick,
  className,
  variant = 'default',
  children,
  defaultExpanded = false,
  hideCount = false,
  alert = false,
  actionIcon,
  actionTooltip,
  onAction,
  labelTooltip,
}: FilterMenuItemProps) => {
  const prefix = prefixClass('q2-filter-menu-item');
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const { state } = useQueueContext();
  const { highlightedFilter } = state;

  // Check if this filter is highlighted (for action-triggered animations)
  const isHighlighted = filterId && highlightedFilter === filterId;

  // Only use droppable for default and child variants with a filterId
  const shouldBeDroppable = filterId && (variant === 'default' || variant === 'child');

  const { isOver, setNodeRef } = useDroppable({
    id: filterId || 'disabled',
    data: {
      filterId,
      canDrop,
    },
    disabled: !shouldBeDroppable,
  });

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (variant === 'parent') {
        setIsExpanded(!isExpanded);
      }
      onClick?.();
    }
  };

  const handleClick = () => {
    if (variant === 'parent') {
      setIsExpanded(!isExpanded);
    }
    onClick?.();
  };

  const showCount = !hideCount && count !== undefined && count > 0;

  return (
    <>
      <div
        ref={shouldBeDroppable ? setNodeRef : undefined}
        className={classNames(
          prefix(),
          prefix(`--${variant}`),
          {
            selected,
            expanded: variant === 'parent' && isExpanded,
            'drop-target': isOver && canDrop,
            'drop-highlight': isHighlighted,
            'drop-invalid': isOver && !canDrop,
            alert,
            disabled: !showCount,
          },
          className
        )}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
      >
        <div className={prefix('left')}>
          {variant !== 'child' && icon && <div className={prefix('icon')}>{icon}</div>}
          {variant === 'child' ? (
            <Tooltip content={labelTooltip || label} showDelay={600}>
              <span className={prefix('label')}>{label}</span>
            </Tooltip>
          ) : (
            <span className={prefix('label')}>{label}</span>
          )}
        </div>
        <div className={prefix('right')}>
          {actionIcon && onAction && (
            <Tooltip content={actionTooltip || ''} showDelay={300}>
              <button
                className={prefix('action-btn')}
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  onAction();
                }}
                aria-label={actionTooltip}
              >
                {actionIcon}
              </button>
            </Tooltip>
          )}
          {showCount && <CountBadge count={count} />}
          {variant === 'parent' && (
            <div className={prefix('chevron')}>{isExpanded ? <IconChevronDown /> : <IconChevron />}</div>
          )}
        </div>
      </div>
      {variant === 'parent' && isExpanded && children && <div className={prefix('children')}>{children}</div>}
    </>
  );
};

export default FilterMenuItem;
